<script setup lang="ts">
    import CanvasWeddingView from '@/components/CanvasWeddingView.vue';
    import BaseButton from '@/components/BaseButton.vue';
    import { useConfigStore } from '@/store/config.store';
    import { EAppStep } from '@/typescript/enums/EAppStep';
    import BaseIcon from '@/components/BaseIcon.vue';
    import { t } from '@/translations/i18n';
    import { Events } from '@/services/Events';
    import { useAnimationControl } from '@/composables/useAnimationControl';
    import { useStepper } from '@/composables/useStepper';
    import { useTemplateStore } from '@/store/template.store';
    import { useZoomControl } from '@/composables/useZoomControl';
    import { TweakPanel } from '@/commons/TweakPanel';
    import { addToCart } from '@/utils/addToCart';
    import IconZoomIn from '@/components/icons/IconZoomIn.vue';
    import { screenshot } from '@/littleWedding/screenshotHelper';
    import { generateUUID } from '@/utils/emptyTemplate';
    import { dataStore } from '@/commons/DataStore';
    import { onMounted } from 'vue';
    import { storeToRefs } from 'pinia';

    const { selectedTemplate } = storeToRefs(useTemplateStore());
    const { currentSide, isAdmin, isMobile, projectId } = storeToRefs(useConfigStore());
    const { isAnimated, toggleAnimation } = useAnimationControl();
    const { isZoomed, toggleZoom, unZoom, zoom } = useZoomControl();
    const { goToStep } = useStepper();

    function goToEditStep() {
        goToStep(EAppStep.EDITION);
    }

    function callScreenshot() {
        const fabricWorld = dataStore.getFabricWorld(currentSide.value);
        screenshot({ selectedTemplate: selectedTemplate.value, fabricWorld });
    }

    onMounted(() => {
        if (!TweakPanel.isInitialized) {
            return;
        }
        const batTest = TweakPanel.getFolderOrTabByName()?.addFolder({
            title: 'test BAT',
            expanded: false,
        });
        batTest
            ?.addButton({
                title: 'get BAT',
            })
            .on('click', () => {
                if (!projectId.value) {
                    projectId.value = generateUUID();
                }
                addToCart(projectId.value).then((res) => {
                    if (!res) {
                        Events.emit('call-toaster', { title: t('error_title'), body: t('error_BAT_generation'), type: 'error' });
                        return;
                    }
                    const namePrefix = 'PMEA_';
                    const extension = `.pdf`;
                    const now = new Date();
                    const date = now.toISOString().split('T')[0];
                    const time = `${now.getHours()}h-${now.getMinutes()}m-${now.getSeconds()}s`;
                    const nameSuffix = `${date}_${time}`;

                    const a = document.createElement('a');
                    const url = URL.createObjectURL(res.pdf);
                    a.href = url;
                    a.download = `${namePrefix}${nameSuffix}${extension}`;
                    a.click();
                });
            });
        batTest
            ?.addButton({
                title: 'get Thumbnail',
            })
            .on('click', () => {
                callScreenshot();
            });
    });
</script>

<template>
    <div :class="[isMobile && 'h-dvh', !isMobile && 'h-full', 'relative hover:cursor-pointer']">
        <div
            :class="[
                isMobile && 'h-dvh',
                !isMobile && 'bottom-8 mr-7 h-full',
                'pointer-events-none absolute right-0 z-10 flex w-full flex-col items-end justify-end gap-2.5 p-4',
            ]"
        >
            <BaseButton
                class="pointer-events-auto h-9 w-9"
                @click="toggleAnimation"
            >
                <span class="sr-only">
                    {{ isAnimated ? $t('play') : $t('pause') }}
                </span>
                <BaseIcon
                    v-if="isAnimated"
                    size="lg"
                    name="PauseIcon"
                />
                <BaseIcon
                    v-else
                    size="lg"
                    name="PlayIcon"
                />
            </BaseButton>
            <BaseButton
                :class="[isZoomed && 'bg-hover-primary', 'pointer-events-auto h-9 w-9']"
                @click="toggleZoom"
            >
                <span class="sr-only">
                    {{ isZoomed ? $t('zoom') : $t('unzoom') }}
                </span>
                <IconZoomIn />
            </BaseButton>
            <BaseButton
                class="pointer-events-auto h-9 w-9"
                @click="zoom"
            >
                <span class="sr-only">
                    {{ $t('zoom') }}
                </span>
                <BaseIcon
                    size="lg"
                    name="MagnifyingGlassPlusIcon"
                />
            </BaseButton>
            <BaseButton
                class="pointer-events-auto h-9 w-9"
                @click="unZoom"
            >
                <span class="sr-only">
                    {{ $t('unzoom') }}
                </span>
                <BaseIcon
                    size="lg"
                    name="MagnifyingGlassMinusIcon"
                />
            </BaseButton>
            <BaseButton
                v-if="isAdmin"
                class="pointer-events-auto h-10 w-10"
                @click="callScreenshot"
            >
                <span class="sr-only">
                    {{ $t('screenshot') }}
                </span>
                <BaseIcon
                    size="lg"
                    name="CameraIcon"
                />
            </BaseButton>
            <BaseButton
                v-if="isMobile"
                class="pointer-events-auto w-full"
                @click="goToEditStep"
            >
                {{ $t('edit_content_label') }}
            </BaseButton>
        </div>
        <CanvasWeddingView :is-mobile />
    </div>
</template>
